<template>
	<b-navbar spaced wrapper-class="container">
		<template slot="brand">
			<b-navbar-item tag="router-link" :to="{ path: '/dashboard' }">
				<img class="logo" alt="moveUP" />
			</b-navbar-item>
		</template>
		<template v-if="user && user._id" slot="start">
			<b-navbar-item tag="router-link" :to="{ path: '/dashboard' }">
				<translate>Dashboard</translate>
			</b-navbar-item>
			<b-navbar-item
				v-if="access && access.scopes.includes('application:read')"
				tag="router-link"
				:to="{ path: '/applications' }"
			>
				<translate>Applications</translate>
			</b-navbar-item>
			<b-navbar-item tag="router-link" :to="{ path: '/users' }"> <translate>Users</translate> </b-navbar-item>
			<b-navbar-item
				tag="router-link" :to="{ path: '/codes' }"> <translate>Registration codes</translate>
			</b-navbar-item>
			<b-navbar-item tag="router-link" :to="{ path: '/platformflavors' }">
				<translate>Platform flavors</translate>
			</b-navbar-item>
			<b-navbar-item
				v-if="access && access.scopes.includes('user:read')"
				tag="router-link"
				:to="{ path: '/templates' }"
			>
				<translate>Templates</translate>
			</b-navbar-item>
		</template>

		<template slot="end">
			<b-navbar-item href="https://support.orthopedics.moveup.care/" target="_blank" rel="noopener">
				<translate>Support</translate>
			</b-navbar-item>

			<!-- Context switcher — hidden during load; plain label if single context, dropdown if multiple -->
			<template v-if="showSwitcher">
				<!-- Single context: plain item, no chevron, subtle green tint (CNTX-03) -->
				<b-navbar-item v-if="!canSwitch" class="context-label">
					{{ selectedContext.name }}
				</b-navbar-item>
				<!-- Multiple contexts: dropdown (CNTX-02) -->
				<b-navbar-dropdown
					v-else
					:label="selectedContext.name"
					class="context-switcher-dropdown"
					data-context-switcher
				>
					<b-navbar-item
						v-for="ctx in visibleContexts"
						:key="ctx._id"
						:class="['context-dropdown-item', { 'is-active-context': selectedContext._id === ctx._id }]"
						data-context-item
						@click.native="selectContext(ctx)"
					>
						{{ ctx.name }}
					</b-navbar-item>
				</b-navbar-dropdown>
			</template>

			<b-navbar-dropdown v-if="user && user._id" :label="$gettext('Account')">
				<b-navbar-item tag="router-link" :to="{ path: '/settings' }">
					<translate>Settings</translate></b-navbar-item
				>
				<b-navbar-item tag="router-link" :to="{ path: '/logout' }">
					<translate>Log out</translate></b-navbar-item
				>
			</b-navbar-dropdown>
		</template>
	</b-navbar>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { errorToast } from '@moveup/app-core/mixins';

export default {
	mixins: [errorToast],

	computed: {
		...mapState('auth', ['user', 'access', 'tokens']),
		...mapState('app', ['contexts', 'selectedContext', 'contextsLoading']),

		isMoveUpAdmin() {
			if (!this.user?._id) return false;
			const moveupRoot = this.contexts.find(ctx => ctx.name === 'moveup' && !ctx.parentContextId);
			if (!moveupRoot) return false;
			const role = moveupRoot.users?.find(u => u.userId === this.user._id)?.contextRole;
			return role === 'moveupadmin';
		},

		visibleContexts() {
			return this.contexts.filter(ctx => {
				const isMoveupRoot = ctx.name === 'moveup' && !ctx.parentContextId;
				if (isMoveupRoot) return this.isMoveUpAdmin;
				return true;
			});
		},

		showSwitcher() {
			return !this.contextsLoading && this.visibleContexts.length > 0;
		},

		canSwitch() {
			return this.visibleContexts.length > 1;
		},
	},

	mounted() {
		if (this.tokens?.accessToken) this.loadContexts().catch(err => this.errorToast(err));
	},

	watch: {
		'tokens.accessToken'(accessToken) {
			if (accessToken) this.loadContexts().catch(err => this.errorToast(err));
		},
	},

	methods: {
		...mapActions('app', ['loadContexts', 'setSelectedContext']),

		selectContext(ctx) {
			this.setSelectedContext(ctx);
		},
	},
};
</script>


<style>
.logo {
	content: var(--logo);
}
</style>

<style scoped>
/* Context switcher — single context label */
.context-label {
	color: hsl(160deg, 40%, 40%);
	font-weight: 500;
	cursor: default;
}

/* Context dropdown items */
.context-dropdown-item.is-active-context {
	background-color: hsl(160deg, 40%, 94%);
	font-weight: 600;
}

.context-dropdown-item:hover:not(.is-active-context) {
	background-color: hsl(0deg, 0%, 96%);
}
</style>
